import { useState } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ChatWidget from '../home/components/ChatWidget';

export default function ITConsultingPage() {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setShowSuccessModal(true);
    const form = e.currentTarget;
    form.reset();
    setCharCount(0);
  };

  const scrollToForm = () => {
    document.getElementById('consultation-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Breadcrumb */}
      <div className="bg-gray-50 py-3 border-b border-gray-100">
        <div className="container mx-auto px-4">
          <div className="flex items-center text-sm">
            <a href="/" className="text-gray-500 hover:text-primary flex items-center cursor-pointer">
              <div className="w-4 h-4 flex items-center justify-center mr-1">
                <i className="ri-home-line"></i>
              </div>
              Home
            </a>
            <div className="w-4 h-4 flex items-center justify-center mx-2 text-gray-400">
              <i className="ri-arrow-right-s-line"></i>
            </div>
            <a href="#" className="text-gray-500 hover:text-primary cursor-pointer">IT Services</a>
            <div className="w-4 h-4 flex items-center justify-center mx-2 text-gray-400">
              <i className="ri-arrow-right-s-line"></i>
            </div>
            <span className="text-primary font-medium">Consulting</span>
          </div>
          <div className="mt-2">
            <a href="/" className="text-sm text-gray-600 hover:text-primary flex items-center cursor-pointer">
              <div className="w-4 h-4 flex items-center justify-center mr-1">
                <i className="ri-arrow-left-line"></i>
              </div>
              Back to Services
            </a>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative w-full">
        <img 
          src="https://public.readdy.ai/ai/img_res/034905eb30e9b906533ad60275280022.jpg" 
          alt="IT Consulting Services" 
          className="w-full h-96 object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white/90 via-white/70 to-transparent"></div>
        <div className="container mx-auto px-4 py-20 absolute inset-0 flex items-center">
          <div className="w-full max-w-2xl">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">IT Consulting Services</h1>
            <p className="text-lg text-gray-700 mb-8">
              Strategic technology guidance to optimize your business operations, drive innovation, and achieve sustainable growth through expert IT consulting.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={scrollToForm}
                className="bg-primary text-white px-6 py-3 rounded-button whitespace-nowrap font-medium hover:bg-primary/90 transition-colors cursor-pointer"
              >
                Schedule Consultation
              </button>
              <button
                onClick={() => setShowPhoneModal(true)}
                className="bg-white border border-gray-300 text-gray-800 px-6 py-3 rounded-button whitespace-nowrap font-medium hover:bg-gray-50 transition-colors cursor-pointer"
              >
                Learn More
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Our Consulting Specialties */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Our Consulting Specialties</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              We provide comprehensive IT consulting services to help businesses navigate complex technology landscapes, make informed decisions, and implement effective solutions aligned with their strategic goals.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* IT Strategy & Planning */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden transition-transform hover:shadow-lg hover:-translate-y-1 border border-gray-100">
              <div className="h-48 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=strategic%20business%20planning%20visualization%20with%20digital%20roadmap%20timeline%20charts%20and%20technology%20architecture%20diagrams%20displayed%20on%20modern%20interface%20clean%20professional%20style%20with%20interconnected%20nodes%20representing%20IT%20infrastructure%20planning%20light%20neutral%20background%20emphasizing%20clarity%20and%20organization&width=800&height=600&seq=itstrategy001&orientation=landscape" 
                  alt="IT Strategy & Planning" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                  <div className="w-6 h-6 flex items-center justify-center text-primary">
                    <i className="ri-strategy-line"></i>
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-3">IT Strategy & Planning</h3>
                <p className="text-gray-600 mb-4">
                  Develop comprehensive technology roadmaps aligned with your business objectives to drive growth and competitive advantage.
                </p>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Technology roadmap development</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">IT governance frameworks</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Strategic technology investment planning</span>
                  </li>
                </ul>
                <a href="#" className="text-primary font-medium flex items-center cursor-pointer hover:underline">
                  Learn More
                  <div className="w-4 h-4 flex items-center justify-center ml-1">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </a>
              </div>
            </div>

            {/* Digital Transformation */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden transition-transform hover:shadow-lg hover:-translate-y-1 border border-gray-100">
              <div className="h-48 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=digital%20transformation%20concept%20showing%20modern%20workspace%20with%20cloud%20computing%20symbols%20artificial%20intelligence%20icons%20and%20connected%20digital%20devices%20representing%20business%20modernization%20sleek%20futuristic%20design%20with%20holographic%20elements%20and%20data%20streams%20light%20background%20conveying%20innovation%20and%20progress&width=800&height=600&seq=digitaltrans001&orientation=landscape" 
                  alt="Digital Transformation" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                  <div className="w-6 h-6 flex items-center justify-center text-primary">
                    <i className="ri-rocket-line"></i>
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-3">Digital Transformation</h3>
                <p className="text-gray-600 mb-4">
                  Navigate your organization's digital evolution with expert guidance on modernizing processes, systems, and customer experiences.
                </p>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Business process optimization</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Legacy system modernization</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Digital customer experience design</span>
                  </li>
                </ul>
                <a href="#" className="text-primary font-medium flex items-center cursor-pointer hover:underline">
                  Learn More
                  <div className="w-4 h-4 flex items-center justify-center ml-1">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </a>
              </div>
            </div>

            {/* Technology Assessment */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden transition-transform hover:shadow-lg hover:-translate-y-1 border border-gray-100">
              <div className="h-48 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=technology%20assessment%20and%20evaluation%20scene%20with%20professional%20analyst%20reviewing%20multiple%20computer%20screens%20displaying%20system%20performance%20metrics%20security%20scans%20and%20infrastructure%20diagrams%20modern%20tech%20office%20environment%20with%20organized%20workspace%20clean%20bright%20lighting%20emphasizing%20thorough%20analysis%20and%20quality%20assurance&width=800&height=600&seq=techassess001&orientation=landscape" 
                  alt="Technology Assessment" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                  <div className="w-6 h-6 flex items-center justify-center text-primary">
                    <i className="ri-search-eye-line"></i>
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-3">Technology Assessment</h3>
                <p className="text-gray-600 mb-4">
                  Comprehensive evaluation of your current technology landscape to identify gaps, opportunities, and optimization potential.
                </p>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">IT infrastructure evaluation</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Security & compliance assessment</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Technology stack optimization</span>
                  </li>
                </ul>
                <a href="#" className="text-primary font-medium flex items-center cursor-pointer hover:underline">
                  Learn More
                  <div className="w-4 h-4 flex items-center justify-center ml-1">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Consulting Process */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Our Consulting Process</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              We follow a structured, collaborative approach to deliver tailored consulting solutions that address your specific business challenges and technology needs.
            </p>
          </div>
          <div className="relative">
            <div className="absolute top-1/2 left-0 right-0 h-1 bg-gray-200 -translate-y-1/2 hidden md:block"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {/* Step 1 */}
              <div className="relative">
                <div className="hidden md:flex absolute top-0 left-1/2 w-10 h-10 bg-primary rounded-full -translate-x-1/2 -translate-y-1/2 z-10 items-center justify-center text-white font-bold">
                  1
                </div>
                <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100 relative z-0 h-full">
                  <div className="md:hidden w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white font-bold mb-4">
                    1
                  </div>
                  <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                    <div className="w-6 h-6 flex items-center justify-center text-primary">
                      <i className="ri-search-line"></i>
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Discovery & Assessment</h3>
                  <p className="text-gray-600">
                    We thoroughly analyze your current technology environment, business objectives, and challenges to establish a clear baseline.
                  </p>
                  <ul className="mt-4 space-y-2">
                    <li className="flex items-start">
                      <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                        <i className="ri-arrow-right-circle-line"></i>
                      </div>
                      <span className="text-gray-700">Stakeholder interviews</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                        <i className="ri-arrow-right-circle-line"></i>
                      </div>
                      <span className="text-gray-700">Systems evaluation</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                        <i className="ri-arrow-right-circle-line"></i>
                      </div>
                      <span className="text-gray-700">Gap analysis</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Step 2 */}
              <div className="relative">
                <div className="hidden md:flex absolute top-0 left-1/2 w-10 h-10 bg-primary rounded-full -translate-x-1/2 -translate-y-1/2 z-10 items-center justify-center text-white font-bold">
                  2
                </div>
                <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100 relative z-0 h-full">
                  <div className="md:hidden w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white font-bold mb-4">
                    2
                  </div>
                  <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                    <div className="w-6 h-6 flex items-center justify-center text-primary">
                      <i className="ri-layout-2-line"></i>
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Strategy Development</h3>
                  <p className="text-gray-600">
                    We create a tailored roadmap with specific recommendations, prioritized initiatives, and a clear implementation framework.
                  </p>
                  <ul className="mt-4 space-y-2">
                    <li className="flex items-start">
                      <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                        <i className="ri-arrow-right-circle-line"></i>
                      </div>
                      <span className="text-gray-700">Solution architecture</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                        <i className="ri-arrow-right-circle-line"></i>
                      </div>
                      <span className="text-gray-700">Technology selection</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                        <i className="ri-arrow-right-circle-line"></i>
                      </div>
                      <span className="text-gray-700">ROI analysis</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Step 3 */}
              <div className="relative">
                <div className="hidden md:flex absolute top-0 left-1/2 w-10 h-10 bg-primary rounded-full -translate-x-1/2 -translate-y-1/2 z-10 items-center justify-center text-white font-bold">
                  3
                </div>
                <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100 relative z-0 h-full">
                  <div className="md:hidden w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white font-bold mb-4">
                    3
                  </div>
                  <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                    <div className="w-6 h-6 flex items-center justify-center text-primary">
                      <i className="ri-file-list-3-line"></i>
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Implementation Planning</h3>
                  <p className="text-gray-600">
                    We develop detailed execution plans with resource requirements, timelines, and change management strategies.
                  </p>
                  <ul className="mt-4 space-y-2">
                    <li className="flex items-start">
                      <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                        <i className="ri-arrow-right-circle-line"></i>
                      </div>
                      <span className="text-gray-700">Project roadmapping</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                        <i className="ri-arrow-right-circle-line"></i>
                      </div>
                      <span className="text-gray-700">Resource planning</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                        <i className="ri-arrow-right-circle-line"></i>
                      </div>
                      <span className="text-gray-700">Risk mitigation</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Step 4 */}
              <div className="relative">
                <div className="hidden md:flex absolute top-0 left-1/2 w-10 h-10 bg-primary rounded-full -translate-x-1/2 -translate-y-1/2 z-10 items-center justify-center text-white font-bold">
                  4
                </div>
                <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100 relative z-0 h-full">
                  <div className="md:hidden w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white font-bold mb-4">
                    4
                  </div>
                  <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                    <div className="w-6 h-6 flex items-center justify-center text-primary">
                      <i className="ri-rocket-line"></i>
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Execution & Support</h3>
                  <p className="text-gray-600">
                    We provide implementation guidance, knowledge transfer, and ongoing support to ensure successful outcomes.
                  </p>
                  <ul className="mt-4 space-y-2">
                    <li className="flex items-start">
                      <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                        <i className="ri-arrow-right-circle-line"></i>
                      </div>
                      <span className="text-gray-700">Implementation oversight</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                        <i className="ri-arrow-right-circle-line"></i>
                      </div>
                      <span className="text-gray-700">Change management</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                        <i className="ri-arrow-right-circle-line"></i>
                      </div>
                      <span className="text-gray-700">Continuous improvement</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Consultant Expertise */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Our Consultant Expertise</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Our team of experienced consultants brings deep industry knowledge, technical expertise, and proven methodologies to help you navigate complex technology challenges.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Industry Expertise */}
            <div className="bg-white p-8 rounded-lg shadow-md border border-gray-100 h-full">
              <h3 className="text-2xl font-semibold mb-6">Industry Expertise</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                    <div className="w-5 h-5 flex items-center justify-center text-primary">
                      <i className="ri-building-line"></i>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-2">Enterprise</h4>
                    <p className="text-gray-600">
                      Extensive experience with large-scale enterprise environments and complex organizational structures.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                    <div className="w-5 h-5 flex items-center justify-center text-primary">
                      <i className="ri-bank-line"></i>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-2">Financial Services</h4>
                    <p className="text-gray-600">
                      Specialized knowledge of banking, insurance, and fintech security and compliance requirements.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                    <div className="w-5 h-5 flex items-center justify-center text-primary">
                      <i className="ri-heart-pulse-line"></i>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-2">Healthcare</h4>
                    <p className="text-gray-600">
                      Deep understanding of healthcare IT systems, HIPAA compliance, and patient data management.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                    <div className="w-5 h-5 flex items-center justify-center text-primary">
                      <i className="ri-shopping-bag-line"></i>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-2">Retail</h4>
                    <p className="text-gray-600">
                      Expertise in omnichannel retail technologies, supply chain systems, and customer experience platforms.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-8">
                <h3 className="text-xl font-semibold mb-4">Certifications & Partnerships</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 flex flex-col items-center text-center">
                    <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-3">
                      <div className="w-6 h-6 flex items-center justify-center text-primary">
                        <i className="ri-microsoft-line"></i>
                      </div>
                    </div>
                    <span className="text-gray-800 font-medium text-sm">Microsoft Gold</span>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 flex flex-col items-center text-center">
                    <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-3">
                      <div className="w-6 h-6 flex items-center justify-center text-primary">
                        <i className="ri-amazon-line"></i>
                      </div>
                    </div>
                    <span className="text-gray-800 font-medium text-sm">AWS Partner</span>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 flex flex-col items-center text-center">
                    <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-3">
                      <div className="w-6 h-6 flex items-center justify-center text-primary">
                        <i className="ri-google-line"></i>
                      </div>
                    </div>
                    <span className="text-gray-800 font-medium text-sm">Google Cloud</span>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 flex flex-col items-center text-center">
                    <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-3">
                      <div className="w-6 h-6 flex items-center justify-center text-primary">
                        <i className="ri-database-2-line"></i>
                      </div>
                    </div>
                    <span className="text-gray-800 font-medium text-sm">Oracle Gold</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Consultant Qualifications */}
            <div className="bg-white p-8 rounded-lg shadow-md border border-gray-100 h-full">
              <h3 className="text-2xl font-semibold mb-6">Consultant Qualifications</h3>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                    <div className="w-5 h-5 flex items-center justify-center text-primary">
                      <i className="ri-user-star-line"></i>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-2">Senior-Level Expertise</h4>
                    <p className="text-gray-600">
                      Our consultants average 15+ years of industry experience with backgrounds at leading technology companies and consulting firms.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                    <div className="w-5 h-5 flex items-center justify-center text-primary">
                      <i className="ri-graduation-cap-line"></i>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-2">Advanced Certifications</h4>
                    <p className="text-gray-600">
                      Our team holds advanced certifications in project management, IT service management, cybersecurity, cloud architecture, and more.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                    <div className="w-5 h-5 flex items-center justify-center text-primary">
                      <i className="ri-book-open-line"></i>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-2">Continuous Learning</h4>
                    <p className="text-gray-600">
                      We invest in ongoing education to stay current with emerging technologies, methodologies, and industry best practices.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-8">
                <h3 className="text-xl font-semibold mb-4">Key Performance Metrics</h3>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <div className="grid grid-cols-2 gap-6">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-primary mb-1">95%</div>
                      <div className="text-sm text-gray-600">Client Satisfaction</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-primary mb-1">200+</div>
                      <div className="text-sm text-gray-600">Projects Completed</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-primary mb-1">30%</div>
                      <div className="text-sm text-gray-600">Avg Cost Savings</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-primary mb-1">85%</div>
                      <div className="text-sm text-gray-600">Repeat Clients</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ready to Transform Section & Form */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left Column - Benefits */}
            <div>
              <h2 className="text-3xl font-bold mb-6">Ready to Transform Your IT Strategy?</h2>
              <p className="text-gray-600 mb-8">
                Contact our team of expert consultants to discuss your business challenges and how our strategic IT consulting services can help you achieve your goals.
              </p>
              <div className="space-y-6 mb-8">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <div className="w-5 h-5 flex items-center justify-center text-primary">
                      <i className="ri-shield-check-line"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Vendor-Neutral Advice</h3>
                    <p className="text-gray-600">
                      We provide unbiased recommendations based on your specific needs, not vendor relationships or commissions.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <div className="w-5 h-5 flex items-center justify-center text-primary">
                      <i className="ri-team-line"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Collaborative Approach</h3>
                    <p className="text-gray-600">
                      We work closely with your team to ensure knowledge transfer and build internal capabilities.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <div className="w-5 h-5 flex items-center justify-center text-primary">
                      <i className="ri-line-chart-line"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Measurable Results</h3>
                    <p className="text-gray-600">
                      We define clear success metrics and deliver tangible business outcomes, not just recommendations.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                <h3 className="text-xl font-semibold mb-4">Our Consulting Impact</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-gray-700 font-medium">Cost Reduction</span>
                      <span className="text-primary font-medium">35%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-primary h-2 rounded-full" style={{ width: '35%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-gray-700 font-medium">Operational Efficiency</span>
                      <span className="text-primary font-medium">42%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-primary h-2 rounded-full" style={{ width: '42%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-gray-700 font-medium">Project Success Rate</span>
                      <span className="text-primary font-medium">94%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-primary h-2 rounded-full" style={{ width: '94%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-gray-700 font-medium">Digital Transformation ROI</span>
                      <span className="text-primary font-medium">312%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-primary h-2 rounded-full" style={{ width: '100%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Form */}
            <div className="bg-white p-8 rounded-lg shadow-md border border-gray-100" id="consultation-form">
              <h3 className="text-2xl font-bold mb-6">Request a Consultation</h3>
              <form onSubmit={handleFormSubmit} data-readdy-form>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="company" className="block text-gray-700 font-medium mb-2">
                      Company Name
                    </label>
                    <input
                      id="company"
                      name="company"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Your company"
                    />
                  </div>
                  <div>
                    <label htmlFor="name" className="block text-gray-700 font-medium mb-2">
                      Contact Person
                    </label>
                    <input
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="your.email@company.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-gray-700 font-medium mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="(123) 456-7890"
                    />
                  </div>
                </div>
                <div className="mb-6">
                  <label htmlFor="requirements" className="block text-gray-700 font-medium mb-2">
                    Consulting Requirements
                  </label>
                  <textarea
                    id="requirements"
                    name="requirements"
                    rows={4}
                    maxLength={500}
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Please describe your business challenges or consulting needs"
                  ></textarea>
                  <div className="mt-1 text-sm text-gray-500">{charCount}/500 characters</div>
                </div>
                <div className="mb-6">
                  <label className="block text-gray-700 font-medium mb-2">Interested Services</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="strategy"
                        name="services"
                        value="IT Strategy & Planning"
                        className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer"
                      />
                      <label htmlFor="strategy" className="ml-2 text-gray-700 cursor-pointer">
                        IT Strategy & Planning
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="digital-transform"
                        name="services"
                        value="Digital Transformation"
                        className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer"
                      />
                      <label htmlFor="digital-transform" className="ml-2 text-gray-700 cursor-pointer">
                        Digital Transformation
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="tech-assessment"
                        name="services"
                        value="Technology Assessment"
                        className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer"
                      />
                      <label htmlFor="tech-assessment" className="ml-2 text-gray-700 cursor-pointer">
                        Technology Assessment
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="implementation"
                        name="services"
                        value="Implementation Support"
                        className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer"
                      />
                      <label htmlFor="implementation" className="ml-2 text-gray-700 cursor-pointer">
                        Implementation Support
                      </label>
                    </div>
                  </div>
                </div>
                <div className="mb-6">
                  <label className="block text-gray-700 font-medium mb-2">Preferred Contact Method</label>
                  <div className="flex flex-wrap gap-4">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="contact-email"
                        name="contact_method"
                        value="Email"
                        className="w-4 h-4 text-primary border-gray-300 focus:ring-primary cursor-pointer"
                      />
                      <label htmlFor="contact-email" className="ml-2 text-gray-700 cursor-pointer">
                        Email
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="contact-phone"
                        name="contact_method"
                        value="Phone"
                        className="w-4 h-4 text-primary border-gray-300 focus:ring-primary cursor-pointer"
                      />
                      <label htmlFor="contact-phone" className="ml-2 text-gray-700 cursor-pointer">
                        Phone
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="contact-meeting"
                        name="contact_method"
                        value="Video Meeting"
                        className="w-4 h-4 text-primary border-gray-300 focus:ring-primary cursor-pointer"
                      />
                      <label htmlFor="contact-meeting" className="ml-2 text-gray-700 cursor-pointer">
                        Video Meeting
                      </label>
                    </div>
                  </div>
                </div>
                <button
                  type="submit"
                  className="bg-primary text-white px-6 py-3 rounded-button whitespace-nowrap font-medium w-full hover:bg-primary/90 transition-colors cursor-pointer"
                >
                  Submit Request
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowSuccessModal(false)}></div>
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 relative">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="w-8 h-8 flex items-center justify-center text-green-500">
                  <i className="ri-check-line text-3xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-2">Request Submitted!</h3>
              <p className="text-gray-600 mb-6">
                Thank you for your consultation request. Our IT consultants will contact you within 24 hours to discuss your needs.
              </p>
              <button
                onClick={() => setShowSuccessModal(false)}
                className="bg-primary text-white px-6 py-2 rounded-lg whitespace-nowrap font-medium hover:bg-primary/90 transition-colors cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phone Modal */}
      {showPhoneModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowPhoneModal(false)}></div>
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 relative">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Contact Us</h3>
              <button onClick={() => setShowPhoneModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-close-line ri-lg"></i>
                </div>
              </button>
            </div>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-primary bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="w-8 h-8 flex items-center justify-center text-primary">
                  <i className="ri-phone-line ri-2x"></i>
                </div>
              </div>
              <div className="text-2xl font-bold text-gray-900 mb-2">+31 (0)6 1074 9083</div>
              <p className="text-gray-600 mb-4">Our IT consultants are available to discuss your needs</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-gray-900 mb-3">Business Hours</h4>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>Monday - Friday:</span>
                  <span className="font-medium">8:00 AM - 6:00 PM EST</span>
                </div>
                <div className="flex justify-between">
                  <span>Saturday:</span>
                  <span className="font-medium">Closed</span>
                </div>
                <div className="flex justify-between">
                  <span>Sunday:</span>
                  <span className="font-medium">Closed</span>
                </div>
              </div>
            </div>
            <a
              href="tel:+31061074908"
              className="w-full bg-primary text-white py-3 rounded-lg whitespace-nowrap font-medium hover:bg-primary/90 flex items-center justify-center cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center mr-2">
                <i className="ri-phone-line"></i>
              </div>
              Call Now
            </a>
          </div>
        </div>
      )}

      <Footer />
      <ChatWidget />
    </div>
  );
}
